import type { ClientMergePreview, ClientMergeSide } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { useCabinetAccess } from "../../../access/use-access"
import { fetchMergePreview, mergeClients, unmergeClient } from "../../../api/duplicates.api"
import { isApiProblem } from "../../../api/problem"
import { cx } from "../../../lib/cx"
import { ProblemScreen } from "../../../shell/states"
import { resolveBranch } from "../../../shell/use-branch"
import { Badge, Button, Skeleton } from "../../../ui"
import { useResource } from "../use-resource"
import styles from "./duplicates.module.css"
import {
  CHOICE_FIELDS,
  FIELD_LABEL,
  type MergeForm,
  type Side,
  changeText,
  defaultForm,
  noShowWord,
  phonesWord,
  primaryCandidates,
  requestOf,
  sideOf,
  summaryOf,
  valueOf,
  visitsWord,
  withMain,
} from "./merge-model"

const ADS_LABEL = { given: "дано", revoked: "отозвано", never: "не запрашивалось" } as const

/**
 * 3-А7 «Объединить карточки» — /clients/merge?left=…&right=…: две карточки рядом,
 * поле за полем, и выбор, что останется.
 *
 * ОБРАТИМОСТЬ СКАЗАНА НА КНОПКЕ И В ИТОГЕ («Объединить · обратимо»): без этого
 * администратор боится нажать, и дубли остаются навсегда.
 *
 * КОНФЛИКТ НЕ ПРОГЛАТЫВАЕТСЯ. Если пока выбирали поля, в карточку добавили запись или
 * телефон, сервер отвечает 409; экран перечитывает обе карточки, говорит, что именно
 * изменилось, и кнопка закрыта, пока человек не посмотрит обновлённый состав.
 */
export function MergeScreen(props: {
  readonly leftId: string
  readonly rightId: string
  readonly onBack: () => void
  readonly onOpenClient: (clientId: string) => void
}): ReactNode {
  const { context } = useCabinetAccess()
  const timeZone = resolveBranch(context, null)?.timezone ?? context.tenant.defaultTimezone

  const loaded = useResource(
    (signal) => fetchMergePreview(props.leftId, props.rightId, signal),
    `merge:${props.leftId}:${props.rightId}`,
  )
  const [preview, setPreview] = useState<ClientMergePreview | null>(null)
  const [form, setForm] = useState<MergeForm | null>(null)
  const [fresh, setFresh] = useState<{ readonly preview: ClientMergePreview; readonly text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [done, setDone] = useState<{ readonly survivorId: string; readonly mergedId: string; readonly name: string } | null>(
    null,
  )
  const [undone, setUndone] = useState(false)

  useEffect(() => {
    if (loaded.data !== null && preview === null) {
      setPreview(loaded.data)
      setForm(defaultForm(loaded.data))
    }
  }, [loaded.data, preview])

  if (loaded.problem !== null && loaded.data === null) {
    return <ProblemScreen problem={loaded.problem} onRetry={loaded.reload} />
  }

  const head = (
    <div className={styles.mergeHead}>
      <button type="button" className={styles.back} onClick={props.onBack}>
        ‹ К списку
      </button>
      <div className={styles.crumbTitle}>Объединить карточки</div>
      <span className={styles.muted}>
        Поле за полем — выберите, что останется. Телефоны, заметки, метки, визиты объединяются без потерь.
      </span>
    </div>
  )

  if (preview === null || form === null) {
    return (
      <div className={styles.screen}>
        <div className={styles.merge}>
          {head}
          <Skeleton width="100%" height={240} radius={10} />
        </div>
      </div>
    )
  }

  const alreadyMerged = [preview.left, preview.right].find((card) => card.mergedIntoId !== null) ?? null
  if (alreadyMerged !== null && done === null) {
    return (
      <div className={styles.screen}>
        <div className={styles.merge}>
          {head}
          <div className={styles.conflict}>
            Карточка «{alreadyMerged.displayName}» уже объединена с другой.
            <button
              type="button"
              className={styles.inlineLink}
              onClick={() => {
                if (alreadyMerged.mergedIntoId !== null) props.onOpenClient(alreadyMerged.mergedIntoId)
              }}
            >
              Открыть её
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (done !== null) {
    return (
      <div className={styles.screen}>
        <div className={styles.merge}>
          {head}
          <div className={styles.summary} role="status">
            {undone ? (
              <>Слияние отменено: обе карточки на месте, визиты и телефоны вернулись к своим владельцам.</>
            ) : (
              <>
                <b>Карточки объединены:</b> осталась «{done.name}». Если ошиблись — слияние можно отменить: вторая
                карточка не удалена, а помечена слитой.
              </>
            )}
          </div>
          {problem === null ? null : <div className={styles.problem}>{problem}</div>}
          <div className={styles.footer}>
            {undone ? null : (
              <Button
                loading={busy}
                onClick={() => {
                  setBusy(true)
                  setProblem(null)
                  unmergeClient(done.mergedId)
                    .then(() => setUndone(true))
                    .catch((error: unknown) => {
                      if (!isApiProblem(error)) throw error
                      setProblem(error.detail ?? error.title)
                    })
                    .finally(() => setBusy(false))
                }}
              >
                Отменить слияние
              </Button>
            )}
            <Button onClick={props.onBack}>К списку дублей</Button>
            <Button variant="primary" onClick={() => props.onOpenClient(undone ? done.mergedId : done.survivorId)}>
              Открыть карточку
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const summary = summaryOf(preview, form)
  const candidates = new Set(primaryCandidates(preview, form.main).map((phone) => phone.id))

  const submit = async (): Promise<void> => {
    const request = requestOf(preview, form)
    if (request === null) {
      setProblem("Выберите основной телефон: на него уходят напоминания")
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      const result = await mergeClients(request)
      setDone({ survivorId: result.survivorId, mergedId: request.mergedId, name: summary.name })
    } catch (error) {
      if (!isApiProblem(error)) throw error
      if (error.status === 409 && error.type.endsWith("merge-version-conflict")) {
        const next = await fetchMergePreview(props.leftId, props.rightId)
        setFresh({ preview: next, text: changeText(preview, next, timeZone) ?? (error.detail ?? error.title) })
        return
      }
      setProblem(
        error.status >= 500
          ? "Объединить не получилось. Обе карточки на месте, ничего не потеряно. Попробуйте ещё раз"
          : (error.detail ?? error.title),
      )
    } finally {
      setBusy(false)
    }
  }

  const sideHead = (side: Side): ReactNode => {
    const letter = side === "left" ? "А" : "Б"
    return form.main === side ? (
      <div className={styles.cell}>
        Карточка {letter}
        <Badge tone="ok" className={styles.mainChip}>
          главная
        </Badge>
      </div>
    ) : (
      <div className={styles.cell}>
        Карточка {letter} · будет помечена как слитая
        <button type="button" className={styles.inlineLink} onClick={() => setForm(withMain(preview, side))}>
          сделать главной
        </button>
      </div>
    )
  }

  return (
    <div className={styles.screen}>
      <div className={styles.merge}>
        {head}
        <div className={styles.table}>
          <div className={cx(styles.row, styles.headRow)}>
            <div className={styles.label} />
            {sideHead("left")}
            {sideHead("right")}
          </div>

          {CHOICE_FIELDS.map((field) => (
            <div key={field} className={styles.row}>
              <div className={styles.label}>{FIELD_LABEL[field]}</div>
              {(["left", "right"] as const).map((side) => (
                <div key={side} className={styles.cell}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={form.choices[field] === side}
                    className={styles.choice}
                    onClick={() => setForm({ ...form, choices: { ...form.choices, [field]: side } })}
                  >
                    <span className={styles.radio} aria-hidden="true" />
                    {valueOf(sideOf(preview, side), field)}
                  </button>
                </div>
              ))}
            </div>
          ))}

          <div className={styles.row}>
            <div className={styles.label}>Телефоны · основной</div>
            {(["left", "right"] as const).map((side) => (
              <div key={side} className={styles.cell}>
                <PhonesCell
                  card={sideOf(preview, side)}
                  isMain={form.main === side}
                  candidates={candidates}
                  primaryPhoneId={form.primaryPhoneId}
                  onPrimary={(id) => setForm({ ...form, primaryPhoneId: id })}
                />
              </div>
            ))}
          </div>

          <AllRow label="Каналы связи" left={channelsOf(preview.left)} right={channelsOf(preview.right)} />
          <AllRow
            label={`Заметки · ${preview.left.notesCount} + ${preview.right.notesCount}`}
            left={notesOf(preview.left)}
            right={notesOf(preview.right)}
          />
          <AllRow
            label="Метки"
            left={preview.left.tags.length === 0 ? "—" : preview.left.tags.join(", ")}
            right={preview.right.tags.length === 0 ? "—" : preview.right.tags.join(", ")}
          />
          <AllRow
            label={`Визиты · ${preview.left.visitsCount} + ${preview.right.visitsCount}`}
            left={sideOf(preview, form.main) === preview.left ? "история целиком, по времени" : "все визиты переедут"}
            right={sideOf(preview, form.main) === preview.right ? "история целиком, по времени" : "все визиты переедут"}
          />
          <AllRow
            label="Согласия"
            left={consentOf(preview.left, summary.ads)}
            right={consentOf(preview.right, summary.ads)}
          />
          <div className={styles.row}>
            <div className={styles.label}>Счётчики</div>
            <div className={styles.cell}>пересчитаются из объединённой истории — вручную не выбираются</div>
            <div className={styles.cell} />
          </div>
          <div className={styles.row}>
            <div className={styles.label}>Ограничение записи</div>
            <div className={styles.cell}>{preview.left.blacklisted ? "есть" : "нет"}</div>
            <div className={styles.cell}>{preview.right.blacklisted ? "есть" : "нет"}</div>
          </div>
        </div>

        <div className={styles.summary}>
          <b>После слияния останется одна карточка:</b> {summary.name} · {phonesWord(summary.phones)} ·{" "}
          {visitsWord(summary.visits)} · {noShowWord(summary.noShows)}
          {summary.tags.length === 0 ? "" : ` · метки ${summary.tags.map((tag) => `«${tag}»`).join(", ")}`} ·
          согласие на рекламу — <b>{ADS_LABEL[summary.ads]}</b>
          {summary.ads === "revoked" ? " (действует то, что даёт меньше прав)" : ""}
          {summary.blacklisted ? " · ограничение онлайн-записи остаётся" : ""}. Счётчики пересчитаются из объединённой
          истории. <b>Если ошиблись — слияние можно отменить:</b> вторая карточка не удаляется, а помечается слитой.
        </div>

        {fresh === null ? null : (
          <div className={styles.conflict} role="alert">
            {fresh.text}
            <button
              type="button"
              className={styles.inlineLink}
              onClick={() => {
                const main = form.main
                setPreview(fresh.preview)
                const next = withMain(fresh.preview, main)
                setForm({ ...next, choices: form.choices })
                setFresh(null)
              }}
            >
              Обновить
            </button>
          </div>
        )}
        {problem === null ? null : <div className={styles.problem}>{problem}</div>}

        <div className={styles.footer}>
          <Button onClick={props.onBack}>Отмена</Button>
          <Button
            variant="primary"
            loading={busy}
            deniedReason={fresh === null ? undefined : "Сначала посмотрите обновлённый состав карточек"}
            onClick={() => void submit()}
          >
            Объединить · обратимо
          </Button>
        </div>
      </div>
    </div>
  )
}

function PhonesCell(props: {
  readonly card: ClientMergeSide
  readonly isMain: boolean
  readonly candidates: ReadonlySet<string>
  readonly primaryPhoneId: string | null
  readonly onPrimary: (id: string) => void
}): ReactNode {
  if (props.card.phones.length === 0) return <span className={styles.muted}>телефонов нет</span>
  return (
    <div className={styles.phoneList}>
      {props.card.phones.map((phone) =>
        props.candidates.has(phone.id) ? (
          <button
            key={phone.id}
            type="button"
            role="radio"
            aria-checked={props.primaryPhoneId === phone.id}
            className={styles.choice}
            onClick={() => props.onPrimary(phone.id)}
          >
            <span className={styles.radio} aria-hidden="true" />
            {phone.phoneMasked}
            {props.primaryPhoneId === phone.id ? " · основной" : ""}
          </button>
        ) : (
          <span key={phone.id} className={styles.muted}>
            {phone.phoneMasked} · такой же есть в главной — станет её дублем
          </span>
        ),
      )}
    </div>
  )
}

function AllRow(props: { readonly label: string; readonly left: string; readonly right: string }): ReactNode {
  return (
    <div className={styles.row}>
      <div className={styles.label}>{props.label}</div>
      <div className={cx(styles.cell, styles.all)}>{props.left}</div>
      <div className={cx(styles.cell, styles.all)}>{props.right}</div>
    </div>
  )
}

const CHANNEL: Readonly<Record<string, string>> = {
  telegram: "Telegram",
  max: "MAX",
  mobile: "приложение",
  email: "e-mail",
  sms: "SMS",
}

function channelsOf(card: ClientMergeSide): string {
  return card.channels.length === 0 ? "—" : card.channels.map((channel) => CHANNEL[channel] ?? channel).join(", ")
}

function notesOf(card: ClientMergeSide): string {
  if (card.notesCount === 0) return "—"
  if (card.pinnedNote === null) return `${card.notesCount} шт.`
  const rest = card.notesCount - 1
  return `${card.pinnedNote} (закреплена)${rest > 0 ? `, ещё ${rest}` : ""}`
}

function consentOf(card: ClientMergeSide, after: "given" | "revoked" | "never"): string {
  const own = `реклама: ${ADS_LABEL[card.adsConsent]}`
  return card.adsConsent === "given" && after === "revoked" ? `${own} → действует отзыв` : own
}

